import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Table from '@mui/material/Table';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { DownloadCloud, Trash2, Play, X, Archive, RotateCcw } from 'react-feather';

import VpMediaTagsList from '@vp-ui/widgets/VpMediaTable/VpMediaTagsList';
import VpMediaInfoMenu from '@vp-ui/widgets/VpMediaTable/VpMediaInfoMenu';
import VpAttachmentInfoMenu from '@vp-ui/widgets/VpMediaTable/VpAttachmentInfoMenu';
import { VpMediaIcon, VpCheckbox, VpRadio } from '@vp-ui';
import { VpCaptionBadge } from '@vp-ui/components/VpCaptionBadge';
import { useMediaTableContext, useForceDownload } from '@src/hooks';
import { useQueries, useQueryClient } from 'react-query';
import {
  fetchCaptions,
  fetchMediaVersions,
  useFetchMediaVersions,
  useDeleteBurnIn,
  useRestoreBurnIn,
  CAPTION_POLLING_INTERVAL_MS,
  CAPTIONS_QUERY_KEY
} from '@src/hooks/react-query/useCaptionsData';
import { useFetchVideoCompilations, useAddMediaToLibrary, useUpdateLibraryFieldMapping } from '@src/hooks/react-query/useVideoCompilationData';
import { useFetchPlugins, useUpdatePluginFieldMapping } from '@src/hooks/react-query/usePluginData';
import VpFieldMappingDialog from '@vp-ui/dialogs/VpFieldMappingDialog/VpFieldMappingDialog';
import { useVpSnackbar } from '@vp-ui/hooks/useVpSnackbar';
import { PLUGINS } from '@src/constants/endpoints';
import { formatVersionLabel, getVersionLanguageLabel } from '@src/helpers/captionVersionHelpers';
import api from '@src/utils/api';
import { connect } from 'react-redux';
import { getIsSelectingSsembleMedia } from '@src/redux/modules/ssemble';

const VpCaptionStatusCell = ({ captions }) => {
  const latestCaption = captions[0] || null;
  if (!latestCaption) return null;
  return <VpCaptionBadge status={latestCaption.status} />;
};

VpCaptionStatusCell.propTypes = { captions: PropTypes.array };

// onSuccess(compilation) is called after a successful add — used by callers that want to navigate
// burnInId (optional): if provided, the burn-in version is associated with this library entry
// campaignElements (optional): DATA-type elements for the field mapping picker
export const VpAddToLibraryButton = ({ mediaId, burnInId, onSuccess, campaignElements = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mappingDialog, setMappingDialog] = useState(null); // { compilation }
  const { data: compilationsData } = useFetchVideoCompilations();
  const { mutate: addMedia, isLoading: isAdding } = useAddMediaToLibrary();
  const { mutate: saveMapping } = useUpdateLibraryFieldMapping();
  const { addSnackbarError, addSnackbarSuccess } = useVpSnackbar();

  const compilations = compilationsData?.records || [];

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const doAdd = (compilation) => {
    addMedia(
      { compilationId: compilation.id, mediaId, burnInId: burnInId || null },
      {
        onSuccess: () => {
          addSnackbarSuccess({ message: `Added to "${compilation.title}"` });
          handleClose();
          if (onSuccess) onSuccess(compilation);
        },
        onError: (e) => addSnackbarError({ error: e })
      }
    );
  };

  const handleAdd = (compilation) => {
    if (compilation.titleElementId || compilation.descriptionElementId) {
      doAdd(compilation);
      return;
    }
    handleClose();
    setMappingDialog({ compilation });
  };

  const handleMappingConfirm = async ({ titleElementId, descriptionElementId }) => {
    const { compilation } = mappingDialog;
    try {
      await new Promise((resolve, reject) =>
        saveMapping(
          { compilationId: compilation.id, titleElementId, descriptionElementId },
          { onSuccess: resolve, onError: reject }
        )
      );
      setMappingDialog(null);
      doAdd(compilation);
    } catch (e) {
      addSnackbarError({ error: e });
    }
  };

  return (
    <>
      <Button size="small" variant="outlined" onClick={handleOpen} disabled={isAdding}>
        {isAdding ? <CircularProgress size={14} /> : 'Add to Library'}
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        {compilations.length === 0 && (
          <MenuItem disabled>No libraries found</MenuItem>
        )}
        {compilations.map((c) => (
          <MenuItem key={c.id} onClick={() => handleAdd(c)}>
            {c.title}
          </MenuItem>
        ))}
      </Menu>
      {mappingDialog && (
        <VpFieldMappingDialog
          open
          targetName={mappingDialog.compilation.title}
          elements={campaignElements}
          onConfirm={handleMappingConfirm}
          onCancel={() => { setMappingDialog(null); doAdd(mappingDialog.compilation); }}
        />
      )}
    </>
  );
};

VpAddToLibraryButton.propTypes = {
  mediaId: PropTypes.string,
  burnInId: PropTypes.string,
  onSuccess: PropTypes.func,
  campaignElements: PropTypes.array
};

// Add captioned video version to a Plugin carousel/gallery
export const VpAddToPluginButton = ({ mediaId, burnInId, campaignElements = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [mappingDialog, setMappingDialog] = useState(null); // { plugin, pluginData }
  const { data: pluginsData } = useFetchPlugins();
  const { mutate: saveMapping } = useUpdatePluginFieldMapping();
  const { addSnackbarError, addSnackbarSuccess } = useVpSnackbar();
  const queryClient = useQueryClient();

  const plugins = pluginsData?.records || [];

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const doAdd = async (plugin, pluginData, fieldMapping = null) => {
    const currentContent = (pluginData.content || []).map((item) => ({
      id: item.id,
      contentType: item.contentType || 'video',
      burnInId: item.burnInId || null
    }));

    if (currentContent.some((c) => c.id === mediaId && (c.burnInId || null) === (burnInId || null))) {
      addSnackbarError({ message: 'Video already in this plugin' });
      handleClose();
      return;
    }

    const existingIdx = currentContent.findIndex((c) => c.id === mediaId);
    const newContent = existingIdx !== -1
      ? currentContent.map((c, i) => i === existingIdx ? { id: mediaId, contentType: 'video', burnInId: burnInId || null } : c)
      : [...currentContent, { id: mediaId, contentType: 'video', burnInId: burnInId || null }];

    await api.put(`${PLUGINS}/${plugin.id}`, {
      name: pluginData.name,
      type: pluginData.type,
      sortOrder: pluginData.sortOrder,
      showVideoPeelLogo: pluginData.showVideoPeelLogo,
      styles: pluginData.styles,
      pluginContent: newContent,
      ...(fieldMapping && {
        titleElementId: fieldMapping.titleElementId,
        descriptionElementId: fieldMapping.descriptionElementId
      })
    });

    queryClient.invalidateQueries([PLUGINS]);
    addSnackbarSuccess({ message: `Added to "${plugin.name}"` });
    handleClose();
  };

  const handleAdd = async (plugin) => {
    setIsAdding(true);
    try {
      const pluginData = await api.get(`${PLUGINS}/${plugin.id}/preview`);
      if (pluginData.titleElementId || pluginData.descriptionElementId) {
        await doAdd(plugin, pluginData);
      } else {
        handleClose();
        setMappingDialog({ plugin, pluginData });
      }
    } catch (e) {
      addSnackbarError({ error: e });
    } finally {
      setIsAdding(false);
    }
  };

  const handleMappingConfirm = async ({ titleElementId, descriptionElementId }) => {
    const { plugin, pluginData } = mappingDialog;
    try {
      await new Promise((resolve, reject) =>
        saveMapping(
          { pluginId: plugin.id, titleElementId, descriptionElementId },
          { onSuccess: resolve, onError: reject }
        )
      );
    } catch (e) {
      addSnackbarError({ error: e });
      return;
    }
    setMappingDialog(null);
    setIsAdding(true);
    try {
      await doAdd(plugin, pluginData, { titleElementId, descriptionElementId });
    } catch (e) {
      addSnackbarError({ error: e });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <>
      <Button size="small" variant="outlined" onClick={handleOpen} disabled={isAdding}>
        {isAdding ? <CircularProgress size={14} /> : 'Add to Plugin'}
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        {plugins.length === 0 && (
          <MenuItem disabled>No plugins found</MenuItem>
        )}
        {plugins.map((p) => (
          <MenuItem key={p.id} onClick={() => handleAdd(p)}>
            {p.name}
            {p.type && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({p.type})
              </Typography>
            )}
          </MenuItem>
        ))}
      </Menu>
      {mappingDialog && (
        <VpFieldMappingDialog
          open
          targetName={mappingDialog.plugin.name}
          elements={campaignElements}
          onConfirm={handleMappingConfirm}
          onCancel={() => {
            const { plugin, pluginData } = mappingDialog;
            setMappingDialog(null);
            setIsAdding(true);
            doAdd(plugin, pluginData).finally(() => setIsAdding(false));
          }}
        />
      )}
    </>
  );
};

VpAddToPluginButton.propTypes = {
  mediaId: PropTypes.string,
  burnInId: PropTypes.string,
  campaignElements: PropTypes.array
};

const VpVersionSubRows = ({ mediaId, thumbnailUrl }) => {
  const [expandedIds, setExpandedIds] = useState({});
  const { data: versionsRaw } = useFetchMediaVersions(mediaId, {
    staleTime: 0,
    refetchInterval: (data) => {
      const items = Array.isArray(data) ? data : [];
      const hasProcessing = items.some((v) => v.status === 'pending' || v.status === 'processing');
      const hasRestoring = items.some((v) => v.restoreStatus === 'restoring');
      return hasProcessing || hasRestoring ? CAPTION_POLLING_INTERVAL_MS : false;
    }
  });
  const { forceDownload } = useForceDownload();
  const { mutate: deleteBurnIn, isLoading: isDeleting } = useDeleteBurnIn(mediaId);
  const { mutate: restoreBurnIn, isLoading: isRestoring } = useRestoreBurnIn(mediaId);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const { addSnackbarSuccess, addSnackbarError, addSnackbarInfo } = useVpSnackbar();

  const versions = Array.isArray(versionsRaw) ? versionsRaw : [];

  if (versions.length === 0) return null;

  const toggleExpand = (burnInId) => {
    setExpandedIds((prev) => ({ ...prev, [burnInId]: !prev[burnInId] }));
  };

  const handleDelete = (burnInId) => {
    if (confirmDeleteId !== burnInId) {
      setConfirmDeleteId(burnInId);
      return;
    }
    deleteBurnIn(burnInId, {
      onSuccess: () => {
        addSnackbarSuccess({ message: 'Captioned version deleted.' });
        setConfirmDeleteId(null);
      },
      onError: (e) => {
        addSnackbarError({ error: e });
        setConfirmDeleteId(null);
      }
    });
  };

  const handleRestore = (burnInId) => {
    restoreBurnIn(burnInId, {
      onSuccess: (data) => {
        if (data?.alreadyInProgress) {
          addSnackbarInfo({ message: 'Restore already in progress. Check back in a few hours.' });
        } else if (data?.available) {
          addSnackbarInfo({ message: 'Video is already restored and ready to download.' });
        } else {
          addSnackbarInfo({ message: 'Restore started. Video will be available in ~5 hours.' });
        }
      },
      onError: (e) => addSnackbarError({ error: e })
    });
  };

  return versions.map((version) => {
    const isCompleted = version.status === 'completed';
    const isProcessing = version.status === 'pending' || version.status === 'processing';
    const isFailed = version.status === 'failed';
    const isArchived = isCompleted && !!version.isArchived;
    const isRestoreInProgress = isArchived && version.restoreStatus === 'restoring';
    const isRestoreAvailable = isArchived && version.restoreStatus === 'available';
    const canDownload = isCompleted && !!version.burnInUrl;
    const versionKey = version.burnInId || version.captionId;
    const isExpanded = !!expandedIds[versionKey];
    const isConfirmingDelete = confirmDeleteId === version.burnInId;
    const fileName = version.burnInUrl
      ? version.burnInUrl.split('/').pop().split('?')[0]
      : `${formatVersionLabel(version)}.mp4`;

    const expiresAt = version.expiresAt ? new Date(version.expiresAt) : null;
    const daysUntilExpiry = expiresAt
      ? Math.max(0, Math.ceil((expiresAt - Date.now()) / (1000 * 60 * 60 * 24)))
      : null;

    return (
      <React.Fragment key={versionKey}>
        <TableRow sx={{ backgroundColor: 'action.hover' }}>
          <TableCell padding="checkbox" />
          <TableCell padding="checkbox" />
          {/* Col 2: Thumbnail — aligns with VpMediaIcon in the media row above */}
          <TableCell sx={{ p: 0.5, verticalAlign: 'middle' }}>
            {canDownload && !isArchived && (
              <Box
                onClick={(e) => { e.stopPropagation(); toggleExpand(versionKey); }}
                sx={{
                  position: 'relative',
                  width: 64,
                  height: 36,
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  flexShrink: 0,
                  backgroundColor: 'grey.900',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover .play-overlay': { opacity: 1 }
                }}
              >
                {thumbnailUrl && (
                  <Box
                    component="img"
                    src={thumbnailUrl}
                    alt="thumbnail"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
                  />
                )}
                <Box
                  className="play-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,0.45)',
                    opacity: thumbnailUrl ? 0 : 1,
                    transition: 'opacity 0.2s'
                  }}
                >
                  {isExpanded ? <X size={14} color="#fff" /> : <Play size={14} color="#fff" />}
                </Box>
              </Box>
            )}
          </TableCell>
          {/* Col 3: Chips + details + restore/delete icons — aligns with Prompt column above */}
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={formatVersionLabel(version)}
                size="small"
                color={isFailed ? 'error' : isCompleted ? 'primary' : 'default'}
                variant="outlined"
              />
              {getVersionLanguageLabel(version) && (
                <Chip
                  label={getVersionLanguageLabel(version)}
                  size="small"
                  variant="outlined"
                />
              )}
              {isArchived && (
                <Chip
                  icon={<Archive size={12} />}
                  label={isRestoreInProgress ? 'Restoring…' : isRestoreAvailable ? 'Restored' : 'Archived'}
                  size="small"
                  color={isRestoreAvailable ? 'success' : 'warning'}
                  variant="outlined"
                />
              )}
              {version.preset && (
                <Typography variant="caption" color="text.secondary">
                  {version.preset} · {version.crop}
                  {version.highlightMode ? ` · ${version.highlightMode}` : ''}
                </Typography>
              )}
              {daysUntilExpiry !== null && daysUntilExpiry <= 14 && (
                <Typography variant="caption" color={daysUntilExpiry <= 3 ? 'error.main' : 'warning.main'}>
                  Expires in {daysUntilExpiry}d
                </Typography>
              )}
              {isProcessing && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircularProgress size={12} />
                  <Typography variant="caption" color="text.secondary">Processing…</Typography>
                </Box>
              )}
              {isRestoreInProgress && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircularProgress size={12} />
                  <Typography variant="caption" color="text.secondary">Restoring from archive…</Typography>
                </Box>
              )}
              {isFailed && (
                <Typography variant="caption" color="error.main">Failed — retry in Caption tab</Typography>
              )}
              {isArchived && !isRestoreInProgress && !isRestoreAvailable && (
                <Tooltip title="Restore from archive (~5 hours)">
                  <IconButton
                    size="small"
                    disabled={isRestoring}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRestore(version.burnInId);
                    }}
                  >
                    <RotateCcw size={16} />
                  </IconButton>
                </Tooltip>
              )}
              {canDownload && !isArchived && (
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      forceDownload(version.burnInUrl, fileName);
                    }}
                  >
                    <DownloadCloud size={16} />
                  </IconButton>
                </Tooltip>
              )}
              {isCompleted && (
                <Tooltip title={isConfirmingDelete ? 'Click again to confirm delete' : 'Delete captioned version'}>
                  <IconButton
                    size="small"
                    color={isConfirmingDelete ? 'error' : 'default'}
                    disabled={isDeleting}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(version.burnInId);
                    }}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </TableCell>
          {/* Col 4-6: empty */}
          <TableCell />
          <TableCell />
          <TableCell />
          {/* Col 7: Add to Library + Add to Plugin — right-aligned to match ⋮ above */}
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
              {canDownload && !isArchived && (
                <>
                  <VpAddToLibraryButton
                    mediaId={mediaId}
                    burnInId={version.burnInId}
                  />
                  <VpAddToPluginButton
                    mediaId={mediaId}
                    burnInId={version.burnInId}
                  />
                </>
              )}
            </Box>
          </TableCell>
        </TableRow>
        {canDownload && !isArchived && isExpanded && (
          <TableRow sx={{ backgroundColor: 'action.hover' }}>
            <TableCell padding="checkbox" />
            <TableCell padding="checkbox" />
            <TableCell colSpan={6} sx={{ pb: 1 }}>
              <video
                key={version.burnInId}
                controls
                autoPlay
                src={version.burnInUrl}
                style={{ width: '100%', maxWidth: 400, maxHeight: 220, borderRadius: 4, display: 'block' }}
              />
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  });
};

VpVersionSubRows.propTypes = { mediaId: PropTypes.string, thumbnailUrl: PropTypes.string };

/**
 * Renders burn-in version rows directly inside the parent table's tbody so that
 * columns share the same widths as the media rows above them.
 */
const VpVersionSubRowsShell = ({ mediaId, thumbnailUrl, hasVersions }) => {
  if (!hasVersions) return null;
  return <VpVersionSubRows mediaId={mediaId} thumbnailUrl={thumbnailUrl} />;
};

VpVersionSubRowsShell.propTypes = { mediaId: PropTypes.string, thumbnailUrl: PropTypes.string, hasVersions: PropTypes.bool };

const headCells = [
  { blank: true, id: 1 },
  { blank: true, id: 2 },
  { blank: true, id: 3 },
  { id: 'prompt', label: 'Prompt' },
  { id: 'type', label: 'Media Type' },
  { id: 'tags', label: 'Tags' },
  { id: 'captions', label: 'Captions' },
  { blank: true, id: 4 },
];

const clickableStyles = { cursor: 'pointer' };

const VpMediaSubTable = ({
  videos = [],
  attachments = [],
  onMediaRowClick,
  isSelectingSsembleMedia,
}) => {
  const {
    onMediaCheck,
    getIsMediaSelected,
    onMediaRadioCheck,
  } = useMediaTableContext();

  const captionQueries = useQueries(
    videos.map((v) => ({
      queryKey: [CAPTIONS_QUERY_KEY, v.mediaId],
      queryFn: fetchCaptions,
      enabled: !!v.mediaId,
      staleTime: 60000
    }))
  );

  const versionQueries = useQueries(
    videos.map((v) => ({
      queryKey: ['captionVersions', v.mediaId],
      queryFn: fetchMediaVersions,
      enabled: !!v.mediaId,
      staleTime: 30000
    }))
  );

  const onCheckboxClick = (e, media) => {
    e.stopPropagation();
    onMediaCheck(media);
  };

  const onRadioClick = (e, media) => {
    e.stopPropagation();
    onMediaRadioCheck(media);
  };

  return (
    <Table>
      <TableHead>
        <TableRow>
          {headCells.map((cell) =>
            cell.blank ? (
              <TableCell key={cell.id} padding="checkbox" />
            ) : (
              <TableCell key={cell.id}>{cell.label}</TableCell>
            )
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {videos.map((media, i) => {
          const captions = Array.isArray(captionQueries[i]?.data) ? captionQueries[i].data : [];
          const versions = Array.isArray(versionQueries[i]?.data) ? versionQueries[i].data : [];
          return (
            <React.Fragment key={media?.mediaId}>
              <TableRow
                hover
                sx={clickableStyles}
                onClick={() => onMediaRowClick(media?.mediaId)}
              >
                <TableCell padding="checkbox" />
                <TableCell padding="checkbox">
                  {isSelectingSsembleMedia ? (
                    <VpRadio
                      onClick={(e) => onRadioClick(e, media)}
                      checked={getIsMediaSelected(media?.mediaId)}
                    />
                  ) : (
                    <VpCheckbox
                      onClick={(e) => onCheckboxClick(e, media)}
                      checked={getIsMediaSelected(media?.mediaId)}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <VpMediaIcon media={media} />
                </TableCell>
                <TableCell>{media?.label}</TableCell>
                <TableCell>
                  <Chip label="video" />
                </TableCell>
                <TableCell>
                  <VpMediaTagsList tags={media?.tags} />
                </TableCell>
                <TableCell>
                  <VpCaptionStatusCell captions={captions} />
                </TableCell>
                <TableCell>
                  <VpMediaInfoMenu media={media} />
                </TableCell>
              </TableRow>
              <VpVersionSubRowsShell
                mediaId={media?.mediaId}
                thumbnailUrl={media?.thumbnailUrl}
                hasVersions={versions.length > 0}
              />
            </React.Fragment>
          );
        })}
        {!isSelectingSsembleMedia && attachments.map((attachment) => (
          <TableRow
            hover
            sx={clickableStyles}
            onClick={() => onMediaRowClick(attachment?.attachmentId)}
            key={attachment?.attachmentId}
          >
            <TableCell padding="checkbox" />
            <TableCell padding="checkbox">
              <VpCheckbox
                onClick={(e) => onCheckboxClick(e, attachment)}
                checked={getIsMediaSelected(attachment?.attachmentId)}
              />
            </TableCell>
            <TableCell>
              <VpMediaIcon media={attachment} />
            </TableCell>
            <TableCell>{attachment.label}</TableCell>
            <TableCell>
              <Chip label={attachment.contentType} />
            </TableCell>
            <TableCell>
              <VpMediaTagsList tags={attachment?.tags} />
            </TableCell>
            <TableCell />
            <TableCell>
              <VpAttachmentInfoMenu media={attachment} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

VpMediaSubTable.propTypes = {
  videos: PropTypes.arrayOf(PropTypes.object),
  attachments: PropTypes.arrayOf(PropTypes.object),
  isSelectingSsembleMedia: PropTypes.bool,
  onMediaRowClick: PropTypes.func
};

const mapStateToProps = (state) => ({
  isSelectingSsembleMedia: getIsSelectingSsembleMedia(state),
});

export default connect(mapStateToProps)(VpMediaSubTable);



// WEBPACK FOOTER //
// ./src/vp-ui/widgets/VpMediaTable/VpMediaSubTable.jsx